import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useClaimAnonymous } from '../hooks/useClaimAnonymous';
import { collectTokens } from '../utils/anonymousRedemptions';
import { Ticket, X, Spinner, CheckCircle } from './icons';

const DISMISS_KEY = 'zxdata.claimBannerDismissed.v1';

/**
 * 登录后顶部条：检测本浏览器有未归入账号的匿名兑换记录时，引导一键认领。
 * 自包含成功态（无需全局 toast）。本会话点 × 后不再自动出现（Redeem「待认领」仍兜底）。
 */
export function AnonymousClaimBanner() {
  const { isAuthenticated } = useAuth();
  const { claim } = useClaimAnonymous();
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setCount(0);
      setDone(null);
      return;
    }
    setCount(collectTokens().length);
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1');
  }, [isAuthenticated]);

  if (!isAuthenticated || dismissed) return null;
  if (count === 0 && !done) return null;

  async function onClaim() {
    setBusy(true);
    try {
      const res = await claim();
      const parts: string[] = [];
      if (res.claimedCount) parts.push(`已认领 ${res.claimedCount} 个数据集到当前账号`);
      const other = res.alreadyOwnedCount + res.invalidCount;
      if (other) parts.push(`${other} 个已在账号或已失效`);
      setDone(parts.join('，') || '没有可认领的记录');
      setCount(0);
    } catch {
      setDone('认领失败，请稍后在兑换页重试');
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }

  return (
    <div className="border-b border-line bg-navy-surface">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2.5 text-sm">
        <Ticket className="h-4 w-4 shrink-0 text-navy" />
        {done ? (
          <span className="flex flex-1 items-center gap-1.5 text-fg-muted">
            <CheckCircle className="h-4 w-4 shrink-0 text-success" />
            {done}
          </span>
        ) : (
          <span className="flex-1 text-fg-muted">
            检测到本浏览器有 <span className="font-semibold text-navy">{count}</span> 个匿名兑换的数据集尚未归入账号。
          </span>
        )}
        {!done && (
          <button
            type="button"
            onClick={onClaim}
            disabled={busy}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-control bg-navy-fill px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-navy-fill-strong disabled:opacity-50"
          >
            {busy && <Spinner className="h-3.5 w-3.5" />}
            一键认领
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="关闭"
          className="shrink-0 rounded-control p-1 text-fg-subtle transition-colors hover:text-fg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
